import { CalendarFilled, VerticalAlignTopOutlined } from '@ant-design/icons'
import { GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { Dropdown, Slider } from 'antd'
import { addDays, endOfDay, subDays } from 'date-fns'
import moment from 'moment'
import { useMemo, useState, useEffect } from 'react'
import { DateRange, DateRangePicker } from 'react-date-range'

type DateRangeFilterProps = {
  setCurrentFilters: (filters: Record<string, any>) => void
  currentFilters: Record<string, any>
  fromKey?: string
  toKey?: string
  getFromStorage?: boolean
  dayRangeStorage?: { back: number; ahead: number }
  setDayRangeStorage?: (value: { back: number; ahead: number }) => void
  min?: number
  max?: number
  sliderMin?: number
  sliderMax?: number
  dateRangeVariation?: boolean
  maxDate?: Date
  minDate?: Date
  defaultDayRangeStorage?: { back: number; ahead: number }
}

export function DateRangeFilter({
  setCurrentFilters,
  currentFilters,
  fromKey = 'From',
  toKey = 'To',
  getFromStorage,
  dayRangeStorage,
  setDayRangeStorage,
  min,
  max,
  sliderMin,
  sliderMax,
  dateRangeVariation = false,
  maxDate,
  minDate,
  defaultDayRangeStorage,
}: DateRangeFilterProps) {
  const [visible, setVisible] = useState(false)
  const isSet = useMemo(() => !!currentFilters?.[fromKey], [currentFilters?.[fromKey]])
  const _maxDate = moment().add(max, 'days').toDate()
  const _minDate = moment().add(min, 'days').toDate()
  const dateChange = (item) => {
    const newFilters = {
      ...currentFilters,
      [fromKey]: moment(item.selection.startDate),
      [toKey]: moment(endOfDay(item.selection.endDate)),
    }

    setCurrentFilters(newFilters)
    dateRangeVariation &&
      setDayRangeStorage({
        [fromKey]: moment(item.selection.startDate),
        [toKey]: moment(endOfDay(item.selection.endDate)),
      })
  }

  const dateFilters = [
    {
      startDate: currentFilters?.[fromKey] && new Date(currentFilters[fromKey]),
      endDate: currentFilters?.[toKey] && new Date(currentFilters[toKey]),
      key: 'selection',
    },
  ]
  const commonProps = {
    onChange: (item) => dateChange(item),
    showSelectionPreview: true,
    moveRangeOnFirstSelection: false,
    months: 2,
    ranges: dateFilters,
    direction: 'horizontal',
    format: formatters.DATE,
    minDate: min ? _minDate : minDate,
    maxDate: max ? _maxDate : maxDate,
  }

  const menu = (
    <>
      {getFromStorage && (
        <DefaultSlider
          fromKey={fromKey}
          toKey={toKey}
          setCurrentFilters={setCurrentFilters}
          dayRangeStorage={dayRangeStorage}
          setDayRangeStorage={setDayRangeStorage}
          min={min}
          max={max}
          sliderMin={sliderMin}
          sliderMax={sliderMax}
          defaultDayRangeStorage={defaultDayRangeStorage}
        />
      )}

      {dateRangeVariation ? <DateRange {...commonProps} /> : <DateRangePicker {...commonProps} />}
    </>
  )

  return (
    <div>
      <Dropdown overlay={menu} onOpenChange={(flag) => setVisible(flag)} open={visible} placement='bottomRight'>
        <GraviButton
          size='large'
          icon={<CalendarFilled />}
          buttonText={
            isSet
              ? `${dateFilters[0]?.startDate?.toLocaleDateString()} - ${dateFilters[0]?.endDate?.toLocaleDateString()}`
              : 'Filter By Date'
          }
          theme2={isSet}
          onClick={(e) => e.preventDefault()}
        ></GraviButton>
      </Dropdown>
    </div>
  )
}

const DefaultSlider = ({
  setCurrentFilters,
  fromKey,
  toKey,
  dayRangeStorage,
  setDayRangeStorage,
  min,
  max,
  sliderMin,
  sliderMax,
  defaultDayRangeStorage,
}) => {
  const [sliderValues, setSliderValues] = useState([
    min > 0 ? null : dayRangeStorage?.back ? parseInt(dayRangeStorage.back) : -3,
    dayRangeStorage?.ahead ? parseInt(dayRangeStorage.ahead) : 1,
  ])

  // Update slider values when dayRangeStorage changes (tab switching)
  useEffect(() => {
    if (dayRangeStorage) {
      setSliderValues([
        min > 0 ? null : dayRangeStorage.back ? parseInt(dayRangeStorage.back) : -3,
        dayRangeStorage.ahead ? parseInt(dayRangeStorage.ahead) : 1,
      ])
    }
  }, [dayRangeStorage, min])

  const applyChanges = (setInStorage = true, values) => {
    if (setInStorage) {
      setDayRangeStorage({ back: sliderValues[0], ahead: sliderValues[1] })
    }
    if (!values) values = [sliderValues[0], sliderValues[1]]
    setCurrentFilters((currentFilters) => ({
      ...currentFilters,
      [fromKey]:
        min > 0
          ? moment(addDays(new Date(), min)).startOf('day')
          : moment(subDays(new Date(), Math.abs(values[0]))).startOf('day'),
      [toKey]: moment(addDays(new Date(), values[1])).endOf('day'),
    }))
  }

  const sliderChange = (value) => {
    if (value[0] === 0 && value[1] === 0) return
    if (value[0] <= 0 && value[1] >= 0) setSliderValues(value)
    else if (value[0] >= min && value[1] <= max) setSliderValues(value)
    if (min > 0 && value) setSliderValues([null, value])
  }
  const resetChanges = () => {
    // Use defaultDayRangeStorage if provided, otherwise fall back to hardcoded defaults
    if (defaultDayRangeStorage) {
      setSliderValues([defaultDayRangeStorage.back, defaultDayRangeStorage.ahead])
      return
    }

    // Legacy fallback logic
    if (min && max) {
      setSliderValues([min, max])
      return
    }

    if (min > 0) setSliderValues([null, max])
    else setSliderValues([-3, 1])
  }

  const setDefault = () => {
    // Use defaultDayRangeStorage if provided, otherwise fall back to hardcoded defaults
    if (defaultDayRangeStorage) {
      setSliderValues([defaultDayRangeStorage.back, defaultDayRangeStorage.ahead])
    } else {
      // Legacy fallback logic
      if (min && max) setSliderValues([min, max])
      if (min > 0) setSliderValues([null, max])
      else setSliderValues([-3, 1])
    }

    applyChanges(false)
  }

  return (
    <Horizontal bg='primary' border='all' alignItems='flex-end' style={{ padding: '0 1em 1em' }}>
      <Vertical flex={3}>
        <GraviButton
          style={{
            border: 'none',
            background: 'transparent',

            maxWidth: 'fit-content',
            paddingLeft: 0,
          }}
          buttonText='DEFAULT WINDOW'
          icon={<VerticalAlignTopOutlined style={{ transform: 'rotate(-90deg)' }} />}
          onClick={setDefault}
        />
        <Texto>Use this setting to load a tailored grid view every time.</Texto>
      </Vertical>

      <Horizontal verticalCenter flex={4} horizontalCenter>
        <Texto className='mt-3' appearance='secondary' weight='bold'>
          <VerticalAlignTopOutlined className='mr-2' style={{ transform: 'rotate(-90deg)' }} />
          {sliderValues[0] === null
            ? ''
            : sliderValues[0] === -1
              ? `${Math.abs(sliderValues[0])} day back`
              : `${Math.abs(sliderValues[0])} days back`}
        </Texto>
        <Slider
          range
          className='date-range-filter-slider mx-4'
          defaultValue={min > 0 ? sliderValues[1] : sliderValues}
          value={min > 0 ? sliderValues[1] : sliderValues}
          max={
            typeof sliderMax !== 'undefined' && sliderMax !== null
              ? sliderMax
              : typeof max !== 'undefined' && max !== null
                ? max
                : 15
          }
          min={
            typeof sliderMin !== 'undefined' && sliderMin !== null
              ? sliderMin
              : typeof min !== 'undefined' && min !== null
                ? min
                : -15
          }
          style={{ width: '200px', marginBottom: 0, marginTop: '1em' }}
          marks={
            min >= 0 && {
              0: {
                style: {
                  transform: 'translate(-50%, -160%)',
                  color: 'var(--gray-400)',
                },
                label: <strong>0</strong>,
              },
            }
          }
          onChange={sliderChange}
        />
        <Texto className='mt-3' appearance='secondary' weight='bold'>
          <VerticalAlignTopOutlined className='mr-2' style={{ transform: 'rotate(90deg)' }} />
          {sliderValues[1] === 1 ? `${sliderValues[1]} day ahead` : `${sliderValues[1]} days ahead`}
        </Texto>
      </Horizontal>
      <Vertical alignItems='space-between' style={{ maxWidth: 'fit-content', padding: '1em 0.5em 0' }}>
        <GraviButton
          buttonText='Reset'
          theme4='true'
          style={{ maxWidth: 'fit-content', marginBottom: '0.5em', height: 'fit-content', padding: '2px 10px' }}
          onClick={resetChanges}
        />
        <GraviButton
          buttonText='Apply'
          theme2
          style={{ width: 'fit-content', height: 'fit-content', padding: '2px 10px' }}
          onClick={applyChanges}
        />
      </Vertical>
    </Horizontal>
  )
}
